import { FC, useCallback, useState } from 'react';
import styled from '@emotion/styled';
import Image from '@base/Image';
import { Spacings, Fonts, mediaQuery } from '@styles/index';
import Button from '@base/Button/Button';
import Paper from '@base/Paper/Paper';
import {
  getDateMonthString,
  getTimeFormatInShortFormWithoutSecs
} from '@utils/dates';
import usePreciseTimer from '@hooks/usePreciseTimer';
import RightChevron from 'aquilla/icons/actions/RightChevron';
import {
  DST,
  DST_STATES,
  GOLD_CARD_MAX,
  GOLD_CARD_MIN,
  PURPLE_CARD_LIMIT
} from './utils';
import useDstClickHandlers from './useDstClickHandlers';

const Container = styled(Paper)`
  position: relative;
  padding: ${Spacings.SPACING_10B};
  ${mediaQuery.uptoMobile} {
    padding: ${Spacings.SPACING_4B};
  }
`;

const Summary = styled.div`
  display: flex;
  padding-bottom: ${Spacings.SPACING_6B};
  border-bottom: 1px solid var(--color-divider);
  ${mediaQuery.uptoMobile} {
    border-bottom: ${Spacings.SPACING_0B};
  }
`;

const ContentDiv = styled.div`
  margin-right: ${Spacings.SPACING_10B};
  flex: 1;
`;

const Heading = styled.div`
  font-size: ${Fonts.H3};
  font-weight: ${Fonts.HEAVY};
  line-height: ${Spacings.SPACING_7B};
  color: var(--color-text-primary);
`;

const SubHeader = styled.div`
  font-size: ${Fonts.H5};
  line-height: 22px;
  color: var(--color-text-secondary);
  margin-top: ${Spacings.SPACING_2B};
`;

type StyledScholarshipProps = {
  backgroundImg: string;
  isReattempt: boolean;
};

const Scholarship = styled.div<StyledScholarshipProps>`
  margin-top: ${Spacings.SPACING_6B};
  height: ${({ isReattempt }) => (isReattempt ? '85px' : '78px')};
  display: flex;
  padding: ${({ isReattempt }) => (isReattempt ? '7px' : '0')}
    ${Spacings.SPACING_8B} 0
    ${({ isReattempt }) => (isReattempt ? '88px' : '144px')};
  align-items: center;
  background: ${({ backgroundImg }) => `url('${backgroundImg}')`};
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_4B};
    width: 100%;
    height: ${Spacings.SPACING_8B};
    display: flex;
    background-size: cover;
    justify-content: center;
    padding: ${Spacings.SPACING_0B};
  }
`;
const WebView = styled.div`
  display: block;
  ${mediaQuery.uptoMobile} {
    display: none;
  }
`;
const MobView = styled.div`
  display: none;
  ${mediaQuery.uptoMobile} {
    display: block;
  }
`;

const Details = styled.div`
  flex: 1;
  ${mediaQuery.uptoMobile} {
    max-width: fit-content;
  }
`;

const Text = styled.div`
  font-weight: ${Fonts.SEMIBOLD};
  font-size: ${Fonts.H7};
  line-height: ${Fonts.H5};
  color: var(--color-text-primary);
  margin-bottom: ${Spacings.SPACING_6};
  ${mediaQuery.uptoMobile} {
    font-weight: 600;
    font-size: ${Spacings.SPACING_3B};
    line-height: ${Spacings.SPACING_4B};
    width: 50%;
    text-align: left;
  }
`;

const ValidText = styled.div`
  ${mediaQuery.uptoMobile} {
    font-weight: 600;
    font-size: ${Spacings.SPACING_3B};
    line-height: ${Spacings.SPACING_4B};
    text-align: right;
    width: 50%;
    color: var(--color-text-secondary);
  }
`;
const Card = styled.div`
  display: none;
  ${mediaQuery.uptoMobile} {
    display: block;
    border: ${Spacings.SPACING_1} solid var(--color-i-divider);
    border-radius: ${Spacings.SPACING_2B};
    padding: ${Spacings.SPACING_4B};
    position: relative;
    bottom: 7px;
  }
`;
const TextAssembler = styled.div`
  display: none;
  ${mediaQuery.uptoMobile} {
    display: flex;
    position: relative;
    bottom: 7px;
  }
`;
const Code = styled.div`
  font-size: ${Fonts.H4};
  font-weight: ${Fonts.SEMIBOLD};
  line-height: ${Fonts.H3};
  color: var(--color-text-primary);
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.P1};
    font-weight: ${Fonts.SEMIBOLD};
    line-height: ${Fonts.H5};
  }
`;

const StyledBtn = styled(Button)`
  width: 139px;
  height: 46px;
  margin-left: ${Spacings.SPACING_2B};
  font-size: ${Fonts.H6};
  ${mediaQuery.uptoMobile} {
    margin-top: 10px;
    width: 100%;
    margin-left: ${Spacings.SPACING_0B};
    margin-bottom: 10px;
  }
`;

const ReattemptBtn = styled(StyledBtn)`
  &:hover {
    background-color: var(--color-base-1);
  }
  ${mediaQuery.uptoMobile} {
    margin-top: 10px;
    width: 100%;
    margin-left: ${Spacings.SPACING_0B};
    border: ${Spacings.SPACING_0B};
    flex-direction: row-reverse;
  }
`;

const ReattemptBtnMob = styled.div`
  display: none;
  ${mediaQuery.uptoMobile} {
    display: flex;
    margin-top: 10px;
    width: 100%;
    margin-left: ${Spacings.SPACING_0B};
    border: ${Spacings.SPACING_0B};
    text-align: right;
    color: black;
    justify-content: center;
    align-items: center;
  }
`;
const ReattemptTag = styled.p`
  display: none;
  ${mediaQuery.uptoMobile} {
    display: block;
    width: fit-content;
    margin-left: ${Spacings.SPACING_0B};
    border: ${Spacings.SPACING_0B};
    text-align: center;
    color: black;
    font-family: AvertaStd, -apple-system, BlinkMacSystemFont, sans-serif;
    font-weight: ${Fonts.HEAVY};
    font-size: ${Fonts.EXTRA_SMALL};
  }
`;
const TimeRemaining = styled.span`
  font-size: ${Fonts.H5};
  line-height: 22px;
  color: var(--color-i-red);
`;

const getBackgroundImage = (discountPercentage) => {
  if (
    discountPercentage > GOLD_CARD_MIN &&
    discountPercentage <= GOLD_CARD_MAX
  ) {
    return '/static/images/dst/background-50.svg';
  }
  if (discountPercentage > PURPLE_CARD_LIMIT) {
    return '/static/images/dst/background-30.svg';
  }
  return '/static/images/dst/background-10.svg';
};

const getMobBackgroundImage = (discountPercentage) => {
  if (
    discountPercentage > GOLD_CARD_MIN &&
    discountPercentage <= GOLD_CARD_MAX
  ) {
    return '/static/images/dst/dst_mob_gold.svg';
  }
  if (discountPercentage > PURPLE_CARD_LIMIT) {
    return '/static/images/dst/dst_mob_purple.svg';
  }
  return '/static/images/dst/dst_mob_silver.svg';
};

type TimeLeftProps = {
  timeRemaining: number;
};

const TimeLeft: FC<TimeLeftProps> = ({ timeRemaining }) => {
  const [time, setTime] = useState(timeRemaining);
  const handler = useCallback(() => {
    setTime((data) => data - 1);
  }, []);

  usePreciseTimer(handler, 1000, true);

  if (time < 60) return null;

  return (
    <TimeRemaining>
      Expires in {getTimeFormatInShortFormWithoutSecs(time, 'hr', 'min')}
    </TimeRemaining>
  );
};

type ResultProps = {
  data: DST;
  goalUID: string;
  goalSlug: string;
  goalName: string;
  lps: string;
};

const Result: FC<ResultProps> = ({
  data,
  goalUID,
  goalSlug,
  goalName,
  lps
}) => {
  // eslint-disable-next-line
  const { header, subHeader, ctaList } = data.extraBlockInfo;
  const label = ctaList?.[0]?.label ?? '';
  const {
    scholarshipCodeDetails: { code, discountPercentage, validTill },
    state,
    attemptsLeft
  } = data.data[0];
  const timeRemaining = Math.round(
    (new Date(validTill).getTime() - new Date().getTime()) / 1000
  );
  const { reTakeTest, getScholarship } = useDstClickHandlers({
    goalUID,
    goalSlug,
    goalName,
    lps
  });

  return (
    <>
      <Container>
        <WebView>
          <Summary>
            <ContentDiv>
              <Image
                src="/dst/congratulations.svg"
                height={12}
                width={115}
                alt="congratulations"
              />
              <Heading>{header}</Heading>
              <SubHeader>
                {subHeader}
                {state === DST_STATES.WON && (
                  <>
                    &nbsp;•&nbsp;
                    {timeRemaining > 24 * 3600 ? (
                      `Valid till ${getDateMonthString(validTill)}`
                    ) : (
                      <TimeLeft timeRemaining={timeRemaining} />
                    )}
                  </>
                )}
              </SubHeader>
            </ContentDiv>
            <Image
              src="/dst/celebration.svg"
              height={84}
              width={68}
              alt="celebration"
            />
          </Summary>
          <Scholarship
            backgroundImg={getBackgroundImage(discountPercentage)}
            isReattempt={state === DST_STATES.REATTEMPT}
          >
            <Details>
              <Text>
                {state === DST_STATES.REATTEMPT
                  ? 'Your discount code'
                  : 'Your scholarship code'}
              </Text>
              <Code>{code}</Code>
            </Details>
            {state === DST_STATES.REATTEMPT && attemptsLeft > 0 && (
              <ReattemptBtn
                // @ts-ignore
                label="Reattempt test"
                variant="hollow"
                color="black"
                onClick={reTakeTest}
              />
            )}
            <StyledBtn
              // @ts-ignore
              label={label}
              variant="filled"
              color="black"
              onClick={() => getScholarship(code)}
            />
          </Scholarship>
        </WebView>
        <MobView>
          <Summary>
            <ContentDiv>
              <Image
                src="/dst/congratulations.svg"
                height={12}
                width={115}
                alt="congratulations"
              />
              <Heading>{header}</Heading>
              <SubHeader>{subHeader}</SubHeader>
            </ContentDiv>
            <Image
              src="/dst/celebration.svg"
              height={84}
              width={68}
              alt="celebration"
            />
          </Summary>
          <Card>
            <TextAssembler>
              <Text>
                {state === DST_STATES.REATTEMPT
                  ? 'Your discount code'
                  : 'Your scholarship code'}
              </Text>
              <ValidText>
                {state === DST_STATES.WON && (
                  <>
                    {timeRemaining > 24 * 3600 ? (
                      `Valid till ${getDateMonthString(validTill)}`
                    ) : (
                      <TimeLeft timeRemaining={timeRemaining} />
                    )}
                  </>
                )}
              </ValidText>
            </TextAssembler>
            <Scholarship
              backgroundImg={getMobBackgroundImage(discountPercentage)}
              isReattempt={state === DST_STATES.REATTEMPT}
            >
              <Details>
                <Code>{code}</Code>
              </Details>
            </Scholarship>
          </Card>
          <StyledBtn
            // @ts-ignore
            label={label}
            variant="filled"
            color="black"
            onClick={() => getScholarship(code)}
          />
          {state === DST_STATES.REATTEMPT && attemptsLeft > 0 && (
            <>
              <ReattemptBtnMob
                // @ts-ignore
                onClick={reTakeTest}
              >
                <ReattemptTag>REATTEMPT TEST</ReattemptTag>
                <RightChevron width={16} height={16} />
              </ReattemptBtnMob>
            </>
          )}
        </MobView>
      </Container>
    </>
  );
};

export default Result;
